import { useEffect, useState } from "react";
import { useForm } from "../../useForm";

import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import Input from "../../../controls/Input";
import SelectMenu from "../../../controls/SelectMenu";
import { makeGetApiCall, makePostApiCall } from "../../../../shared/makeApiCall";

const PRODUCT_TYPE_OPTIONS = [
  { value: "solmaz", label: "Solmaz" },
  { value: "sun", label: "Sun Only product" },
];

const initialFValues = {
  product_type: PRODUCT_TYPE_OPTIONS[0],
  product: null,
  coremark_sku: "",
  vendor_pn: "",
};

const isSunExclusive = (sku) =>
  sku?.sun_exclusive === 1 ||
  sku?.sun_exclusive === true ||
  sku?.sun_exclusive === "1";

export default function CoreMarkSkusForm(props) {
  const { index, coreMarkSku, setOpen, actionCall, isEdit } = props;

  const { values, setValues, handleInputChange } = useForm(initialFValues, false);
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadProducts = async (productTypeValue, skuForEdit = null) => {
    setLoadingProducts(true);
    let options = [];

    if (productTypeValue === "sun") {
      const callResult = await makeGetApiCall(`getSunProductsForDropdown`);
      if (callResult.success) {
        options = callResult.data ?? [];
      }
    } else {
      const callResult = await makePostApiCall(`fetchProductsForDropdown`, {});
      if (callResult.success) {
        options = callResult.data ?? [];
      }
    }

    setProductOptions(options);
    setLoadingProducts(false);

    if (skuForEdit) {
      const selected =
        productTypeValue === "sun"
          ? options.find((o) => o.value === skuForEdit.sun_product_id) ?? null
          : options.find(
              (o) =>
                o.value === skuForEdit.product_id ||
                o.plu === skuForEdit.solmaz_plu
            ) ?? null;
      setValues((current) => ({ ...current, product: selected }));
    }

    return options;
  };

  useEffect(() => {
    if (isEdit && coreMarkSku) {
      const sunExclusive = isSunExclusive(coreMarkSku);
      const productType = sunExclusive
        ? PRODUCT_TYPE_OPTIONS[1]
        : PRODUCT_TYPE_OPTIONS[0];

      setValues({
        product_type: productType,
        product: null,
        coremark_sku: coreMarkSku.coremark_sku ?? "",
        vendor_pn: coreMarkSku.vendor_pn ?? "",
      });
      loadProducts(productType.value, coreMarkSku);
      return;
    }

    loadProducts(PRODUCT_TYPE_OPTIONS[0].value);
  }, [coreMarkSku, isEdit]);

  const onProductTypeChange = (e) => {
    const productType = e.target.value;
    setValues((current) => ({
      ...current,
      product_type: productType,
      product: null,
    }));
    loadProducts(productType?.value);
  };

  const buildPayload = () => {
    const isSun = values.product_type?.value === "sun";
    return {
      coremark_sku: values.coremark_sku,
      vendor_pn: values.vendor_pn,
      solmaz_plu: isSun
        ? values.product?.label ?? ""
        : values.product?.plu ?? "",
      sun_product_id: isSun ? values.product?.value ?? null : null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await actionCall(coreMarkSku, payload, index);
      } else {
        await actionCall(payload);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
        <div className="px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              {isEdit && coreMarkSku
                ? `Edit CoreMark SKU - ${coreMarkSku.solmaz_plu || coreMarkSku.name_en || ""}`
                : "Add new CoreMark SKU"}
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={() => setOpen({ childComponent: null, open: false })}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div className="relative mt-6 flex-1 px-4 sm:px-6 space-y-3">
          <SelectMenu
            handleInputChange={onProductTypeChange}
            name="product_type"
            options={PRODUCT_TYPE_OPTIONS}
            value={values.product_type}
            title="Product type"
          />
          <SelectMenu
            handleInputChange={handleInputChange}
            name="product"
            options={productOptions}
            value={values.product}
            title={loadingProducts ? "Product (loading...)" : "Product"}
            isClearable={true}
          />
          <Input
            label="CoreMark SKU"
            name="coremark_sku"
            onChange={handleInputChange}
            value={values.coremark_sku}
            labelOn={true}
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
          />
          <Input
            label="Vendor PN"
            name="vendor_pn"
            onChange={handleInputChange}
            value={values.vendor_pn}
            labelOn={true}
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
          />
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
          onClick={() => setOpen({ childComponent: null, open: false })}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <div className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {isEdit ? "Saving..." : "Creating..."}
            </div>
          ) : isEdit ? (
            "Save"
          ) : (
            "Create"
          )}
        </button>
      </div>
    </div>
  );
}
